/**
 * Composable for inactivity-based session timeout
 *
 * Tracks user activity via DOM events and Vue Router navigation.
 * After INACTIVITY_TIMEOUT_MS of no activity, the session is ended.
 *
 * Activity that resets the timer:
 *  - Mouse movement, clicks, mouse button presses
 *  - Keyboard input
 *  - Touch events (mobile)
 *  - Route navigation (page changes)
 *  - Explicit calls to resetActivity() (e.g. on save operations)
 *
 * To change the inactivity window, edit INACTIVITY_TIMEOUT_MS in session.config.ts.
 */

import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import {
  updateLastActivity,
  getLastActivityTimestamp,
  isInactivityExpired,
  clearLastActivity,
} from '@/utils/session.utils'
import { INACTIVITY_TIMEOUT_MS, INACTIVITY_CHECK_INTERVAL_MS } from '@/utils/session.config'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click'] as const

/** Throttle localStorage writes to at most once per 10 seconds during continuous input */
const PERSIST_THROTTLE_MS = 10_000

export function useInactivityTimeout(onTimeout?: () => void) {
  const authStore = useAuthStore()
  const router = useRouter()

  let intervalId: ReturnType<typeof setInterval> | null = null
  let routerUnsubscribe: (() => void) | null = null

  // Track last localStorage persist time; initialise to -Infinity so the first event always persists
  let lastPersistMs = Number.NEGATIVE_INFINITY

  // Throttled activity handler: updates localStorage at most once every PERSIST_THROTTLE_MS.
  // The in-memory lastPersistMs is updated immediately so inactivity checks stay accurate.
  const handleActivity = () => {
    const now = Date.now()
    if (now - lastPersistMs >= PERSIST_THROTTLE_MS) {
      lastPersistMs = now
      updateLastActivity()
    }
  }

  /**
   * Manually signal user activity (e.g. called from a save operation).
   */
  function resetActivity(): void {
    updateLastActivity()
  }

  /**
   * Check whether the user has been inactive too long.
   * Stops monitoring and triggers the timeout handler if so.
   */
  function checkInactivity(): void {
    if (!authStore.isAuthenticated) return

    if (isInactivityExpired(INACTIVITY_TIMEOUT_MS)) {
      stopMonitoring()
      clearLastActivity()
      onTimeout?.()
    }
  }

  /**
   * Begin tracking user activity and scheduling inactivity checks.
   */
  function startMonitoring(): void {
    // Guard against double-registration
    if (intervalId !== null) return

    // Seed the timestamp if this is a fresh session with no prior activity recorded
    if (getLastActivityTimestamp() === null) {
      updateLastActivity()
    }

    // Listen to user input events on the document
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Reset activity on every route navigation (page change)
    routerUnsubscribe = router.afterEach(() => {
      if (authStore.isAuthenticated) {
        updateLastActivity()
      }
    })

    // Periodic check
    intervalId = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL_MS)
  }

  /**
   * Stop tracking user activity and cancel the inactivity check.
   */
  function stopMonitoring(): void {
    ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, handleActivity)
    })

    routerUnsubscribe?.()
    routerUnsubscribe = null

    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  // Auto lifecycle hooks — callers can also control start/stop explicitly
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startMonitoring()
    }
  })

  onUnmounted(() => {
    stopMonitoring()
  })

  return {
    resetActivity,
    startMonitoring,
    stopMonitoring,
  }
}
